/**
 * Коды стран для телефонного поля формы записи (Шаг 6). Храним только ISO 3166-1 alpha-2
 * и телефонный код: названия стран даёт Intl.DisplayNames на языке формы, поэтому в
 * словарях переводов их нет и бандл виджета не растёт (бюджет 50 КБ gzip).
 *
 * Несколько стран делят один код (+1 — вся NANP, +7 — RU и KZ): в E.164 они неразличимы,
 * выбор влияет только на то, что видит клиент.
 */
import type { Locale } from './domain.js';

// prettier-ignore
export const DIAL_CODES = {
  AD: '376', AE: '971', AF: '93', AG: '1', AI: '1', AL: '355', AM: '374', AO: '244',
  AR: '54', AS: '1', AT: '43', AU: '61', AW: '297', AX: '358', AZ: '994', BA: '387',
  BB: '1', BD: '880', BE: '32', BF: '226', BG: '359', BH: '973', BI: '257', BJ: '229',
  BL: '590', BM: '1', BN: '673', BO: '591', BQ: '599', BR: '55', BS: '1', BT: '975',
  BW: '267', BY: '375', BZ: '501', CA: '1', CD: '243', CF: '236', CG: '242', CH: '41',
  CI: '225', CK: '682', CL: '56', CM: '237', CN: '86', CO: '57', CR: '506', CU: '53',
  CV: '238', CW: '599', CY: '357', CZ: '420', DE: '49', DJ: '253', DK: '45', DM: '1',
  DO: '1', DZ: '213', EC: '593', EE: '372', EG: '20', ER: '291', ES: '34', ET: '251',
  FI: '358', FJ: '679', FK: '500', FM: '691', FO: '298', FR: '33', GA: '241', GB: '44',
  GD: '1', GE: '995', GF: '594', GG: '44', GH: '233', GI: '350', GL: '299', GM: '220',
  GN: '224', GP: '590', GQ: '240', GR: '30', GT: '502', GU: '1', GW: '245', GY: '592',
  HK: '852', HN: '504', HR: '385', HT: '509', HU: '36', ID: '62', IE: '353', IL: '972',
  IM: '44', IN: '91', IQ: '964', IR: '98', IS: '354', IT: '39', JE: '44', JM: '1',
  JO: '962', JP: '81', KE: '254', KG: '996', KH: '855', KI: '686', KM: '269', KN: '1',
  KP: '850', KR: '82', KW: '965', KY: '1', KZ: '7', LA: '856', LB: '961', LC: '1',
  LI: '423', LK: '94', LR: '231', LS: '266', LT: '370', LU: '352', LV: '371', LY: '218',
  MA: '212', MC: '377', MD: '373', ME: '382', MF: '590', MG: '261', MH: '692', MK: '389',
  ML: '223', MM: '95', MN: '976', MO: '853', MP: '1', MQ: '596', MR: '222', MS: '1',
  MT: '356', MU: '230', MV: '960', MW: '265', MX: '52', MY: '60', MZ: '258', NA: '264',
  NC: '687', NE: '227', NF: '672', NG: '234', NI: '505', NL: '31', NO: '47', NP: '977',
  NR: '674', NU: '683', NZ: '64', OM: '968', PA: '507', PE: '51', PF: '689', PG: '675',
  PH: '63', PK: '92', PL: '48', PM: '508', PR: '1', PS: '970', PT: '351', PW: '680',
  PY: '595', QA: '974', RE: '262', RO: '40', RS: '381', RU: '7', RW: '250', SA: '966',
  SB: '677', SC: '248', SD: '249', SE: '46', SG: '65', SH: '290', SI: '386', SJ: '47',
  SK: '421', SL: '232', SM: '378', SN: '221', SO: '252', SR: '597', SS: '211', ST: '239',
  SV: '503', SX: '1', SY: '963', SZ: '268', TC: '1', TD: '235', TG: '228', TH: '66',
  TJ: '992', TK: '690', TL: '670', TM: '993', TN: '216', TO: '676', TR: '90', TT: '1',
  TV: '688', TW: '886', TZ: '255', UA: '380', UG: '256', US: '1', UY: '598', UZ: '998',
  VA: '39', VC: '1', VE: '58', VG: '1', VI: '1', VN: '84', VU: '678', WF: '681',
  WS: '685', YE: '967', YT: '262', ZA: '27', ZM: '260', ZW: '263',
} as const satisfies Record<string, string>;

/**
 * Шаблон национального номера по телефонному коду: «#» — цифра, остальное — разделители.
 * Ключ — код страны, а не ISO: у всей зоны +1 формат один, у RU и KZ — тоже.
 * Только распространённые направления; для остальных маска не применяется, номер
 * остаётся таким, как его ввели.
 * TODO: если понадобится точная маска и проверка длины для всех стран — libphonenumber-js
 * (metadata.min ≈ 20 КБ gzip при бюджете виджета 50 КБ).
 */
// prettier-ignore
export const NUMBER_FORMATS: Record<string, string> = {
  '1': '(###) ###-####',  '7': '### ###-##-##',   '20': '## #### ####',   '27': '## ### ####',
  '30': '### ### ####',   '31': '# ########',     '32': '### ## ## ##',   '33': '# ## ## ## ##',
  '34': '### ## ## ##',   '36': '## ### ####',    '39': '### ### ####',   '40': '### ### ###',
  '41': '## ### ## ##',   '43': '### ######',     '44': '#### ######',    '45': '## ## ## ##',
  '46': '##-### ## ##',   '47': '### ## ###',     '48': '### ### ###',    '49': '#### #######',
  '51': '### ### ###',    '52': '## #### ####',   '53': '# ### ####',     '54': '## ####-####',
  '55': '## #####-####',  '56': '# #### ####',    '57': '### ### ####',   '58': '###-#######',
  '60': '##-### ####',    '61': '### ### ###',    '62': '###-####-####',  '63': '### ### ####',
  '64': '## ### ####',    '65': '#### ####',      '66': '## ### ####',    '81': '## #### ####',
  '82': '## #### ####',   '84': '### ### ####',   '86': '### #### ####',  '90': '### ### ## ##',
  '91': '##### #####',    '92': '### #######',    '94': '## ### ####',    '98': '### ### ####',
  '212': '###-######',    '213': '### ## ## ##',  '216': '## ### ###',    '234': '### ### ####',
  '251': '## ### ####',   '254': '### ######',    '255': '### ### ###',   '256': '### ######',
  '263': '## ### ####',   '351': '### ### ###',   '352': '### ### ###',   '353': '## ### ####',
  '354': '### ####',      '355': '### ### ###',   '356': '#### ####',     '357': '## ######',
  '358': '## ### ####',   '359': '## ### ####',   '370': '### #####',     '371': '## ### ###',
  '372': '#### ####',     '373': '## ### ###',    '374': '## ######',     '375': '## ###-##-##',
  '377': '## ## ## ##',   '380': '## ### ## ##',  '381': '## #######',    '382': '## ######',
  '385': '## ### ####',   '386': '## ### ###',    '389': '## ### ###',    '420': '### ### ###',
  '421': '### ### ###',   '852': '#### ####',     '853': '#### ####',     '855': '## ### ###',
  '880': '####-######',   '886': '### ### ###',   '960': '###-####',      '961': '## ### ###',
  '962': '# #### ####',   '964': '### ### ####',  '965': '#### ####',     '966': '## ### ####',
  '968': '#### ####',     '971': '## ### ####',   '972': '##-###-####',   '973': '#### ####',
  '974': '#### ####',     '976': '## ## ####',    '977': '###-#######',   '992': '## ### ####',
  '993': '## ######',     '994': '## ### ## ##',  '995': '### ## ## ##',  '996': '### ######',
  '998': '## ### ## ##',
};

export type CountryCode = keyof typeof DIAL_CODES;

export const isCountryCode = (value: string): value is CountryCode => value in DIAL_CODES;

/**
 * Страна, выбранная в поле по умолчанию. Страны у клиники в модели данных нет, поэтому
 * берём её из языка формы; клиент может поменять выбор.
 * TODO: когда в настройках клиники появится страна — брать её оттуда.
 */
export const DEFAULT_COUNTRY: Record<Locale, CountryCode> = { en: 'US', ru: 'RU', hy: 'AM' };
